import { createContext, useContext } from 'react'

/**
 * Imperative camera-focus callback shape.
 *
 * `instant` snaps the camera directly to the target with no spring
 * animation — used by the §3.2 launch-time recenter, which *establishes*
 * the initial view rather than animating from a user gesture. A spring
 * there both reads as a jarring cold-load lurch and leaves the camera
 * mid-animation when visual-regression tests screenshot the default view
 * (non-deterministic captures). Tap-driven focus omits the flag and keeps
 * the spring.
 */
export type MapFocusFn = (
  target: { canvasX: number; canvasY: number },
  options?: { instant?: boolean },
) => void

/**
 * Imperative camera-focus seam.
 *
 * MetroMapCanvas owns the camera (`focusOn` is a canvas-local callback,
 * not store state — pan/zoom bypass React reconciliation). The TASK-391
 * geolocation controller needs to recenter the map *without* mutating
 * `selectedStation` / `routeSelection` (the "granted + from not modified"
 * §3.3 branch is a silent recenter). It does so through this handle:
 * MetroMapCanvas registers its `focusOn` via {@link MapFocusHandle.register}
 * on mount, the controller invokes it via {@link MapFocusHandle.focus}.
 *
 * Exposed as methods (rather than a mutable `.current` field) so the
 * shared object stays immutable from every caller's point of view —
 * the registered callback lives in a closure inside the provider.
 */
export interface MapFocusHandle {
  /** MetroMapCanvas registers its `focusOn`; pass `null` to unregister. */
  register(fn: MapFocusFn | null): void
  /** Invokes the registered focus callback, or no-ops when none is set. */
  focus(target: { canvasX: number; canvasY: number }, options?: { instant?: boolean }): void
}

/**
 * Builds a {@link MapFocusHandle} backed by a private closure variable.
 * Created once per `GeolocationController` instance.
 */
export function createMapFocusHandle(): MapFocusHandle {
  let registered: MapFocusFn | null = null
  return {
    register(fn) {
      registered = fn
    },
    focus(target, options) {
      registered?.(target, options)
    },
  }
}

export const MapFocusContext = createContext<MapFocusHandle | null>(null)

/**
 * Reads the shared {@link MapFocusHandle}. Returns `null` when no
 * provider is mounted (e.g. unit tests that render in isolation).
 */
export function useMapFocusHandle(): MapFocusHandle | null {
  return useContext(MapFocusContext)
}
